import { useEffect, useState } from 'react';
import { Check, Loader2, AlertCircle, Cloud } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function SaveIndicator({
  state,
  lastSavedAt,
  className,
}: {
  state: SaveState;
  lastSavedAt: Date | null;
  className?: string;
}) {
  const { t } = useT();
  const [, force] = useState(0);
  useEffect(() => {
    if (!lastSavedAt) return;
    const id = setInterval(() => force(x => x + 1), 5000);
    return () => clearInterval(id);
  }, [lastSavedAt]);

  let icon: React.ReactNode, label: string, tone: string;
  if (state === 'saving') {
    icon = <Loader2 className="h-3.5 w-3.5 animate-spin" />;
    label = t('save.saving');
    tone = 'text-brand-600 dark:text-brand-300';
  } else if (state === 'error') {
    icon = <AlertCircle className="h-3.5 w-3.5" />;
    label = t('save.error');
    tone = 'text-rose-600 dark:text-rose-300';
  } else if (state === 'saved' && lastSavedAt) {
    icon = <Check className="h-3.5 w-3.5" />;
    label = relativeLabel(lastSavedAt, t);
    tone = 'text-emerald-600 dark:text-emerald-400';
  } else {
    icon = <Cloud className="h-3.5 w-3.5" />;
    label = t('save.idle');
    tone = 'text-ink-500';
  }

  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', tone, className)}>
      {icon}
      <span>{label}</span>
    </span>
  );
}

function relativeLabel(d: Date, t: (k: 'save.saved_just' | 'save.saved_s' | 'save.saved_min' | 'save.saved_h', vars?: Record<string, string | number>) => string): string {
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 5)    return t('save.saved_just');
  if (s < 60)   return t('save.saved_s', { n: s });
  if (s < 3600) return t('save.saved_min', { n: Math.floor(s / 60) });
  return t('save.saved_h', { n: Math.floor(s / 3600) });
}
